/* eslint-disable import/newline-after-import */
import actions from 'redux-form/es/actions';
import moment from 'moment';
import * as actionTypes from 'actions/actionTypes';
import {
  resetLoanForm, setLegacyBalance, setRejection,
  submitInitLoanStep,
  goToSpecificStep,
} from 'actions/loan';
import history from 'router/history';
import { mwSendPageStatus } from 'actions/mobileWalletApp';
import { getAnalyticsObject } from 'utils/gaAnalytics';
import {
  generateConsents,
  getErrorCode,
  getRedirectUrlSS,
  normalizeMsisdn,
  removeNonDigitCharacters,
} from 'utils/helpers';
import store from 'router/store';
import { onIframeMessage, sendMessageToParent } from 'actions/integration';
import { bdsTrackingAvailable, removeBdsTracking } from 'utils/bdsTracking';
import { putMarketingConsents } from 'actions/marketingConsents';
import {
  cacheQueryString,
  getLocalStorageItem,
  getSignUpGateData,
  removeLocalStorageItem,
  removePersistentItem,
  saveLocalStorageItem,
  savePersistentItem,
  saveSignUpGate,
  testLocalStorageSupport,
} from 'utils/localRepository';
import queryStringParser from 'utils/queryStringParser';
import { reset as resetReduxForm } from 'redux-form';
import sessionRepository from 'utils/sessionRepository';
import getCookiesManager from 'utils/cookiesManager';
import { STEP_LOAN_PURPOSE, STEP_SUBMIT } from 'consts/loan/steps';
import { PRODUCT_TYPES } from 'consts/products';
import { EMAIL_VERIFICATION_STATUS } from 'consts/emailVerificationStatus';
import { track } from './analytics';
import { setConfirmationKeyIfExists, setImpersonationKeyIfExists } from './impersonation';
import getAnalyticsObjectAU from '../utils/analytics/getAnalyticsObjectAU';
import { REGISTRATION_CONSENTS_PAYLOAD } from '../consts/loan/AU';
import { getAdditionalInformation, getApiGatewayStatementOfAccount, getApiGatewayPdfDocument } from './selfservice';

const {
  reset, change, clearFields, setSubmitFailed, stopSubmit,
} = actions;

function displayAuthError(message) {
  return { type: actionTypes.ERROR_MESSAGE, message };
}

export function registerLT(data) {
  sessionRepository.saveItem('authMethod', 'password');
  return {
    type: actionTypes.REGISTER_LT,
    data,
  };
}

export function setExpiredPageStatus() {
  return {
    type: actionTypes.SET_EXPIRED_PAGE,
  };
}

export function setConfirmationKey(confirmationKey) {
  return {
    type: actionTypes.SET_CONFIRMATION_KEY,
    data: confirmationKey,
  };
}

export function setImpersonationKey(impersonationKey) {
  return {
    type: actionTypes.SET_IMPERSONATION_KEY,
    data: impersonationKey,
  };
}

export function resetLoginFlag() {
  return {
    type: actionTypes.RESET_LOGIN_FLAG,
  };
}

function getSmartIDStatus(url, requestTimeout, temporaryFailureTimeout, startTime, identityNumber) {
  return {
    types: [
      actionTypes.SMART_ID_STATUS,
      actionTypes.SMART_ID_STATUS_SUCCESS,
      actionTypes.SMART_ID_STATUS_FAIL,
    ],
    data: {
      temporaryFailureTimeout,
      startTime,
      identityNumber,
    },
    promise: (client) => client.get(
      url,
      {
        timeout: requestTimeout,
        hideError: true,
        authRequest: true,
        preventLogout: true,
      }
    ),
    afterSuccess: (dispatch, getState, response) => {
      if (response.status === 204) {
        // eslint-disable-next-line max-len -- node upgrade
        dispatch(getSmartIDStatus(url, requestTimeout, temporaryFailureTimeout, startTime, identityNumber));
      }
    },
    afterFailure: (dispatch, getState, response) => {
      const timeSpent = Date.now() - startTime;
      const isTimeout = !response.status;
      let shouldRequestAgain = isTimeout;
      if (timeSpent < temporaryFailureTimeout) {
        if (response.status === 503) {
          shouldRequestAgain = true;
        }
        if (shouldRequestAgain) {
          // eslint-disable-next-line max-len -- node upgrade
          dispatch(getSmartIDStatus(url, requestTimeout, temporaryFailureTimeout, startTime, identityNumber));
        }
      }
    },
  };
}

export function toggleAuthMethod(data) {
  return {
    type: actionTypes.TOGGLE_AUTH_METHOD,
    data,
  };
}

export function setApplyGuarantor(data) {
  return {
    type: actionTypes.SET_APPLY_GUARANTOR,
    data,
  };
}

function triggerMigratedUserPasswordRecoveryLT(dispatch, username) {
  dispatch(change('password-recovery-form', 'passwordResetIdentifier', username));
  history.replace('/password-recovery/');
  return {
    type: actionTypes.MIGRATED_USER_PASSWORD_RECOVERY_LT,
  };
}

export function login(username, password) {
  let normalizedUsername = username;
  if (COUNTRY === 'AU') {
    if (!username.includes('@') && username.startsWith('04')) {
      normalizedUsername = normalizeMsisdn(username);
    }
  }
  return {
    types: [
      actionTypes.LOGIN,
      actionTypes.LOGIN_SUCCESS,
      actionTypes.LOGIN_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication`,
      { password, username: normalizedUsername },
      { hideError: true, authRequest: true, preventLogout: true }
    ),
    afterSuccess: (dispatch, getState, response) => {
      if (SELF_SERVICE) {
        const { accessToken: { value: token }, customer: { customerNumber } } = response.data;
        dispatch(getAdditionalInformation(token, customerNumber));
      }
    },
    afterFailure: (dispatch, getState, response) => {
      const { data: { errCode } } = response;
      if (COUNTRY === 'AU' && errCode === 1002) {
        dispatch(setSubmitFailed('loan', 'username', 'login_pass'));
      }
      if (COUNTRY === 'LT' && errCode === 992) {
        dispatch(triggerMigratedUserPasswordRecoveryLT(dispatch, normalizedUsername));
      }
    },
  };
}

export function registerMX(ssn, number1, number2) {
  const registrationData = {
    ssn,
    msisdn: `+521${number1.replace(/[^0-9.]/g, '')}`,
  };
  if (number2) {
    registrationData.msisdn2 = `+521${number2.replace(/[^0-9.]/g, '')}`;
  }

  return {
    types: [
      actionTypes.REGISTER_MX,
      actionTypes.REGISTER_MX_SUCCESS,
      actionTypes.REGISTER_MX_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/user-registration/register-mx/register`,
      registrationData,
      { hideError: true, authRequest: true, preventLogout: true }
    ),
    afterFailure: (dispatch) => {
      const signUpGateData = getSignUpGateData();
      if (signUpGateData) {
        dispatch(change('authentication-form', 'username', signUpGateData.sugEmail));
      }
    },
  };
}

export function registerPL(data) {
  const {
    firstName, lastName, email, ssn, msisdn, password,
  } = data;
  return {
    types: [
      actionTypes.REGISTER_PL,
      actionTypes.REGISTER_PL_SUCCESS,
      actionTypes.REGISTER_PL_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/user-registration/register-pl`,
      {
        firstName,
        lastName,
        email,
        ssn,
        msisdn: `+${msisdn.replace(/[^0-9.]/g, '')}`,
      },
      { hideError: true, authRequest: true, preventLogout: true }
    )
      .then(() => client.put(`${API_HOST}/customer-pl/password`, { password })),
  };
}

export function getBanksListLT(banksListOnly = false) {
  return {
    types: [
      actionTypes.GET_BANKS_LIST_LT,
      actionTypes.GET_BANKS_LIST_LT_SUCCESS,
      actionTypes.GET_BANKS_LIST_LT_FAIL,
    ],
    promise: (client) => {
      const listBanksLTRequest = client.get(`${API_HOST}/authentication/user-registration/list-banks-lt`);
      if (!banksListOnly) {
        return Promise.all([
          listBanksLTRequest,
          client.get(`${API_HOST}/authentication/user-registration/get-payment-data?frontendVersion=WEB_2_0`),
        ]);
      }
      return listBanksLTRequest;
    },
  };
}

export function startSignup(msisdn, ssn, password) {
  let data = { msisdn, ssn };
  const config = { hideError: true, authRequest: true, preventLogout: true };
  let url = `${API_HOST}/authentication/user-registration/register-init`;
  if (COUNTRY === 'AU') {
    url = `${API_HOST}/authentication/user-registration/register-au/register-init`;
    data = { msisdn: normalizeMsisdn(msisdn) };
  } else if (COUNTRY === 'LT') {
    url = `${API_HOST}/authentication/user-registration/register-init-lt-c24`;
    data.msisdn = normalizeMsisdn(msisdn);
    data.password = password;
    config.ssn = ssn;
  } else if (COUNTRY === 'LV') {
    data.msisdn = normalizeMsisdn(msisdn);
    const isCrediteaLV = window.location.href.indexOf('creditea.lv') !== -1;
    if (isCrediteaLV) {
      config.headers = { 'X-Sms-Channel': 'Creditea' };
    }
  }
  return {
    types: [
      actionTypes.SIGNUP_START,
      actionTypes.SIGNUP_START_SUCCESS,
      actionTypes.SIGNUP_START_FAIL,
    ],
    promise: (client) => client.post(
      url,
      data,
      config
    ),
    afterSuccess: (dispatch) => {
      if (COUNTRY === 'LT') {
        dispatch(getBanksListLT(true));
      }
    },
    meta: {
      analyticsLV: () => (
        getAnalyticsObject('register')
      ),
    },
  };
}

export function finishStartSignup() {
  return {
    type: actionTypes.FINISH_SIGNUP_START,
  };
}

export function confirmSignup(data) {
  let url = `${API_HOST}/authentication/user-registration/register-confirm-${COUNTRY.toLowerCase()}`;
  let payload = {
    msisdn: data.msisdn,
    ssn: data.SSN,
    otp: data.otp,
  };
  if (COUNTRY === 'AU') {
    url = `${API_HOST}/authentication/user-registration/register-au/register-confirm`;
    delete payload.ssn;
    payload.msisdn = normalizeMsisdn(payload.msisdn);
  } else if (COUNTRY === 'LT') {
    // url = `${url}-c24?frontendVersion=WEB_2_0${PRODUCT_IL ? '_IL' : ''}`;
    url = `${API_HOST}/authentication/user-registration/register-confirm-lt-c24`;
    payload = {
      msisdn: normalizeMsisdn(data.msisdn),
      ssn: data.SSN,
      otp: data.otp,
      dataProcessingConsent: data.dataProcessingConsent,
      consent: data.consent,
      politicallyExposedPerson: data.politicallyExposed === 'yes',
      firstName: data.firstName,
      lastName: data.lastName,
      idenfyConsent: data.idenfyConsent,
    };
    if (data.email) {
      payload.email = data.email;
    }
    if (data.password) {
      payload.password = data.password;
    }
  } else {
    if (COUNTRY === 'LV') {
      payload.msisdn = normalizeMsisdn(payload.msisdn);
    }

    payload = {
      ...payload,
      firstName: data.firstName,
      lastName: data.lastName,
      gender: data.gender,
      password: data.password,
    };
  }
  return {
    types: [
      actionTypes.SIGNUP_CONFIRM,
      actionTypes.SIGNUP_CONFIRM_SUCCESS,
      actionTypes.SIGNUP_CONFIRM_FAIL,
    ],
    promise: (client) => client.post(
      url,
      payload,
      { hideError: true, authRequest: true }
    ),
    // afterSuccess: (dispatch, getState) => {
    afterSuccess: (dispatch) => {
      // TODO: probably not valid anymore
      // if (COUNTRY === 'LT') {
      //   dispatch(putMarketingConsents(getState(), data));
      //   const queryString = location.search;
      //   const queryParameters = queryStringParser.parseAndMakeKeysLowerCase(queryString);
      //   if (queryParameters && queryParameters.channel) {
      //     cacheQueryString(queryString, { days: 3 });
      //   }
      // }
      if (COUNTRY === 'LT') {
        dispatch(resetReduxForm('authentication-form'));
      }
      if (COUNTRY === 'AU') {
        const queryString = location.search;
        const queryParameters = queryStringParser.parseAndMakeKeysLowerCase(queryString);
        if (queryParameters && queryParameters.status) {
          cacheQueryString(queryString);
        }
      }
    },
    afterFailure: () => {
      if (COUNTRY === 'LT') {
        const authMethod = sessionRepository.getItem('authMethod');
        if (authMethod) {
          sessionRepository.removeItem('authMethod');
        }
      }
    },
    meta: {
      analyticsAU: () => (
        getAnalyticsObject('registerStart')
      ),
    },
  };
}

export function registerES(data) {
  const url = `${API_HOST}/authentication/user-registration/register-es/register`;
  const payload = {
    ssn: data.SSN,
    msisdn: normalizeMsisdn(data.msisdn),
  };
  return {
    types: [
      actionTypes.REGISTER_ES,
      actionTypes.REGISTER_ES_SUCCESS,
      actionTypes.REGISTER_ES_FAIL,
    ],
    promise: (client) => client.post(
      url,
      payload,
      { hideError: true, authRequest: true, preventLogout: true }
    ),
  };
}

export function registerCZ(data) {
  const url = `${API_HOST}/authentication/user-registration/register-cz/register`;
  const payload = {
    ssn: removeNonDigitCharacters(data.SSN),
    msisdn: normalizeMsisdn(data.msisdn),
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
  };
  return {
    types: [
      actionTypes.REGISTER_CZ,
      actionTypes.REGISTER_CZ_SUCCESS,
      actionTypes.REGISTER_CZ_FAIL,
    ],
    promise: (client) => client.post(
      url,
      payload,
      { hideError: true, authRequest: true }
    ),
    afterFailure: (dispatch, getState, response) => {
      if (getErrorCode(response) === 2803) {
        dispatch(setRejection(response));
      }
    },
  };
}

export function putAdditionalConsents() {
  return {
    types: [
      actionTypes.PUT_ADDITIONAL_CONSENT,
      actionTypes.PUT_ADDITIONAL_CONSENT_SUCCESS,
      actionTypes.PUT_ADDITIONAL_CONSENT_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/customer/additional-consent`,
      REGISTRATION_CONSENTS_PAYLOAD
    ),
  };
}

export function completeSignupAU(data) {
  const { privacyPolicy } = data;
  const isErrorShownOnFirstStep = (response) => {
    const errorCode = getErrorCode(response);

    return [1490, 1491, 1492, 1455, 1472].includes(errorCode);
  };
  return {
    types: [
      actionTypes.SIGNUP_COMPLETE,
      actionTypes.SIGNUP_COMPLETE_SUCCESS,
      actionTypes.SIGNUP_COMPLETE_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/user-registration/register-au/register-complete`,
      {
        firstName: data.firstname,
        middleName: data.middlename,
        lastName: data.lastName,
        dateOfBirth: [
          parseInt(data.birthYear, 10),
          parseInt(data.birthMonth, 10),
          parseInt(data.birthDay, 10),
        ],
        msisdn: normalizeMsisdn(data.msisdn),
        email: data.email,
        otp: data.otp,
        password: data.newPassword,
        instantMessenger: data.instantMessenger === 'yes',
        productTypeOnCreation: PRODUCT_IL ? 'INSTALLMENT' : 'CREDIT_LINE',
        brokerName: localStorage.getItem('brokerName') || null,
      },
      {
        authRequest: true,
        shouldHideErrorForResponse: isErrorShownOnFirstStep,
      }
    ),
    afterSuccess: (dispatch, getState) => {
      const { marketingConsents } = getState();

      // eslint-disable-next-line max-len -- node upgrade
      const nextData = marketingConsents.reduce((acc, { consentName }) => ({
        ...acc,
        [consentName]: true,
      }), { ...data });

      if (privacyPolicy) {
        dispatch(putMarketingConsents(getState(), nextData));
      }

      dispatch(putAdditionalConsents());
    },
    afterFailure: (dispatch, getState, response) => {
      if (isErrorShownOnFirstStep(response)) {
        dispatch(
          clearFields(
            'loan',
            false,
            false,
            'otp',
            'newPassword',
            'newPasswordConfirm',
            'employedAdult',
            'privacyPolicy',
            'identityCheckConsent'
          )
        );
        const code = getErrorCode(response);
        const validMsisdnCodes = code === 1491 || code === 1472;

        if (code === 1455) {
          dispatch(change('authentication-form', 'username', data.email));
        }
        if (validMsisdnCodes) {
          const msisdn = data.msisdn.replace(/\s/g, '');
          dispatch(change('authentication-form', 'username', msisdn));
        }
      }
    },
    meta: {
      analyticsAU: (storeState) => (
        getAnalyticsObjectAU('registration', storeState)
      ),
    },
  };
}

export function makeOneCentPayment(bank, md5) {
  return {
    types: [
      actionTypes.BANK_LOGIN_START,
      actionTypes.BANK_LOGIN_START_SUCCESS,
      actionTypes.BANK_LOGIN_START_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/authentication/banks_lt/one-cent-payment-lt?bank=${bank}&md5=${md5}`,
      { hideError: true }
    ),
  };
}

export function getOneCentPaymentStatus(customerId) {
  return {
    types: [
      actionTypes.ONE_CENT_PAYMENT_STATUS_START,
      actionTypes.ONE_CENT_PAYMENT_STATUS_SUCCESS,
      actionTypes.ONE_CENT_PAYMENT_STATUS_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/authentication/banks_lt/one-cent-payment-status-lt?customer_id=${customerId}`,
      { hideError: true }
    ),
  };
}

function getSmartIDAppConfirmationStatus({ url, timeToRequestAgain }) {
  return {
    types: [
      actionTypes.SMART_ID_APP_CONFIRM_STATUS,
      actionTypes.SMART_ID_APP_CONFIRM_STATUS_SUCCESS,
      actionTypes.SMART_ID_APP_CONFIRM_STATUS_FAIL,
    ],
    promise: (client) => client.post(
      url,
      { authRequest: true }
    ),
    afterSuccess: (dispatch, getState, response) => {
      if (response.data.status === 'OK') {
        const currentState = getState();
        const { authentication } = currentState;
        dispatch(getOneCentPaymentStatus(authentication.id));
      } else {
        setTimeout(() => {
          dispatch(getSmartIDAppConfirmationStatus({ url, timeToRequestAgain: 5000 }));
        }, timeToRequestAgain);
      }
    },
  };
}

export function startSmartIDAppConfirmation() {
  return {
    types: [
      actionTypes.SMART_ID_APP_CONFIRM_START,
      actionTypes.SMART_ID_APP_CONFIRM_SUCCESS,
      actionTypes.SMART_ID_APP_CONFIRM_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/signing/smart-id/`
    ),
    afterSuccess: (dispatch, getState, response) => {
      const { token } = response.data;
      const url = `${API_HOST}/authentication/signing/smart-id/${token}`;
      const {
        authentication: { id: customerId, isOldCustomer },
        loan: { id: applicationId },
      } = getState();
      dispatch(getSmartIDAppConfirmationStatus(
        { url, timeToRequestAgain: 5000 }
      ));
      dispatch(track('checkout', {
        analyticsLT: () => ({
          page: 'verification_pending',
          offerSelected: parseFloat(sessionStorage.getItem('offerSelected')),
          userID: customerId,
          applicationID: applicationId,
          clientType: isOldCustomer ? 'Returning Client' : 'New Client',
        }),
      }));
    },
  };
}

export function startPinReset(bank, token) {
  return {
    types: [
      actionTypes.BANK_LOGIN_START,
      actionTypes.BANK_LOGIN_START_SUCCESS,
      actionTypes.BANK_LOGIN_START_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/banks_${COUNTRY.toLowerCase()}/identify/mw-reset-pin/${bank}`,
      { callbackUrl: location.origin + location.pathname },
      {
        hideError: true,
        authRequest: true,
        headers: { 'X-MW-token': token },
      }
    ),
  };
}

export function startRegisterMW(bank, token) {
  const requestConfig = { hideError: true, authRequest: true };
  if (token) {
    requestConfig.headers = { 'X-MW-token': token };
  }

  return {
    types: [
      actionTypes.BANK_LOGIN_START,
      actionTypes.BANK_LOGIN_START_SUCCESS,
      actionTypes.BANK_LOGIN_START_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/banks_${COUNTRY.toLowerCase()}/identify/mw-register/${bank}`,
      { callbackUrl: location.origin + location.pathname },
      requestConfig
    ),
  };
}

export function confirmPhoneNumber(msisdn, url = null, otpSended = null) {
  let promise = (client) => client.put(
    `${API_HOST}/customer/msisdn`,
    { msisdn }
  );
  if (url) {
    promise = (client) => client.post(url, { msisdn });
  }

  return {
    types: [
      actionTypes.GET_OTP_START,
      actionTypes.GET_OTP_START_SUCCESS,
      actionTypes.GET_OTP_START_FAIL,
    ],
    promise,
    afterSuccess: () => {
      if (otpSended) {
        setTimeout(() => {
          otpSended(false);
        }, 30000);
      }
    },
    afterFailure: () => {
      otpSended?.(false);
    },
  };
}

export function reidentifyPhoneNumber(msisdn) {
  const url = `${API_HOST}/customer/identification/msisdn-verification/start`;
  return {
    types: [
      actionTypes.GET_REIDENTIFY_OTP_START,
      actionTypes.GET_REIDENTIFY_OTP_START_SUCCESS,
      actionTypes.GET_REIDENTIFY_OTP_START_FAIL,
    ],
    promise: (client) => client.post(url, { msisdn }),
  };
}

export function impersonate(key, broker, confirmationKey, payLater, redirectionUrl) {
  return {
    types: [
      actionTypes.IMPERSONATION,
      actionTypes.IMPERSONATION_SUCCESS,
      actionTypes.IMPERSONATION_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/impersonation`,
      { key },
      { hideError: false, authRequest: true }
    ),
    afterSuccess: (dispatch, getState, response) => {
      if (SELF_SERVICE) {
        const { accessToken: { value: token }, customer: { customerNumber } } = response.data;
        dispatch(getAdditionalInformation(token, customerNumber));
      }

      setConfirmationKeyIfExists({
        dispatch,
        setConfirmationKey,
        confirmationKey,
      });
      setImpersonationKeyIfExists({
        dispatch,
        setImpersonationKey,
        impersonationKey: key,
      });
    },
    afterFailure: (dispatch, getState, response) => {
      const { status } = response;
      if (COUNTRY === 'CZ' && status === 401) {
        dispatch(setExpiredPageStatus(true));
      }
    },
  };
}

export function redirectToSS(query) {
  window.location.href = `${getRedirectUrlSS()}${query || ''}`;
}

export function getOcmStatus() {
  return {
    types: [
      actionTypes.OCM_STATUS,
      actionTypes.OCM_STATUS_SUCCESS,
      actionTypes.OCM_STATUS_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/authentication/ocm-status`
    ),
    afterSuccess: (dispatch, getState, response) => {
      dispatch(setLegacyBalance(response.data.legacyBalance));
    },
  };
}

export function impersonateOCM(key) {
  return {
    types: [
      actionTypes.IMPERSONATION,
      actionTypes.IMPERSONATION_SUCCESS,
      actionTypes.IMPERSONATION_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/open-contract-migration`,
      { key },
      { hideError: false, authRequest: true }
    ),
    afterSuccess: (dispatch) => {
      setImpersonationKeyIfExists({
        dispatch,
        setImpersonationKey,
        impersonationKey: key,
      });
      dispatch(getOcmStatus());

      const location = `/${history.getCurrentLocation().pathname}`;
      history.replace(location);
    },
  };
}

export function getUserSession(skipAfterSuccess = false, customerUuidForGetStatementOfAccount = '', stateOfAccountDocumentId = null, statementOfAccountsFileNameDates = '') {
  const {
    impersonationKey,
    confirmationKey,
  } = queryStringParser.parse(location.search);

  return {
    types: [
      actionTypes.RESTORE_SESSION,
      actionTypes.RESTORE_SESSION_SUCCESS,
      actionTypes.RESTORE_SESSION_FAIL,
    ],
    promise: async (client) => {
      if (confirmationKey && impersonationKey) {
        await client.delete(
          `${API_HOST}/authentication`,
          { hideError: true, authRequest: true }
        );
      }
      return client.get(`${API_HOST}/authentication`, { hideError: true, authRequest: true });
    },
    afterSuccess: (dispatch, getState, response) => {
      const shouldCheckAdditionalInformation = SELF_SERVICE && !skipAfterSuccess;
      const shouldGetTransactionsData = SELF_SERVICE && customerUuidForGetStatementOfAccount;
      const shouldGetPdfDocument = SELF_SERVICE && stateOfAccountDocumentId;
      const { accessToken: { value: token }, customer: { customerNumber } } = response.data;

      if (shouldCheckAdditionalInformation) {
        dispatch(getAdditionalInformation(token, customerNumber));
      }

      if (shouldGetTransactionsData) {
        dispatch(getApiGatewayStatementOfAccount(token, customerUuidForGetStatementOfAccount));
      }

      if (shouldGetPdfDocument) {
        dispatch(getApiGatewayPdfDocument(token, stateOfAccountDocumentId, statementOfAccountsFileNameDates));
      }

      if (skipAfterSuccess) {
        return;
      }
      if (EMBEDDED) {
        dispatch(sendMessageToParent({
          messageType: 'LOAN_APP_EVENT',
          eventType: `${actionTypes.RESTORE_SESSION_SUCCESS}`,
        }));
      }
    },
    afterFailure: (dispatch) => {
      const invalidRecoveryEmail = getLocalStorageItem('invalidRecoveryEmail');
      if (invalidRecoveryEmail) {
        dispatch(change('authentication-form', 'email', invalidRecoveryEmail));
        removeLocalStorageItem('invalidRecoveryEmail');
      }
    },
    meta: {
      analyticsEE: (storeState, action) => (
        getAnalyticsObject('0.*', storeState, action)
      ),
      analyticsPL: (storeState, action) => (
        getAnalyticsObject('0.*', storeState, action)
      ),
      analyticsLV: (storeState, action) => (
        getAnalyticsObject('0.*', storeState, action)
      ),
      analyticsFI: (storeState, action) => (
        getAnalyticsObject('startNewApp', storeState, action)
      ),
      analyticsAU: (storeState, action) => (
        getAnalyticsObject('registerComplete', storeState, action)
      ),
    },
  };
}

function verifyMobileIDLogin(data) {
  return {
    types: [
      actionTypes.MOBILE_ID_VERIFY,
      actionTypes.MOBILE_ID_VERIFY_SUCCESS,
      actionTypes.MOBILE_ID_VERIFY_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}/mobile_id/verify`, data, {
      timeout: 5 * 60 * 1000,
      hideError: true,
    }),
    afterSuccess: (dispatch) => {
      dispatch(getUserSession());
    },
  };
}

export function startMobileIDLogin(phone, SSN) {
  const msisdn = normalizeMsisdn(phone);
  return {
    types: [
      actionTypes.MOBILE_ID_START,
      actionTypes.MOBILE_ID_START_SUCCESS,
      actionTypes.MOBILE_ID_START_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/mobile_id/start_auth`,
      { msisdn, SSN },
      { hideError: true, authRequest: true }
    ),
    afterSuccess: (dispatch, getState, response) => {
      if (!response.data.exception) {
        dispatch(verifyMobileIDLogin(response.data));
      }
    },
  };
}

export function startSmartIDLogin(identityNumber) {
  const startTime = Date.now();
  return {
    types: [
      actionTypes.SMART_ID_START,
      actionTypes.SMART_ID_START_SUCCESS,
      actionTypes.SMART_ID_START_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/authentication/smart-id`,
      { identityNumber },
      { hideError: true, authRequest: true }
    ),
    afterSuccess: (dispatch, getState, response) => {
      const { requestTimeout, temporaryFailureTimeout, token } = response.data;
      const url = `${API_HOST}/authentication/smart-id/${token}`;
      // eslint-disable-next-line max-len
      dispatch(getSmartIDStatus(url, requestTimeout, temporaryFailureTimeout, startTime, identityNumber));
    },
  };
}

export function registerSmartIdSubmit(url, otp) {
  return {
    types: [
      actionTypes.SIGNUP_CONFIRM,
      actionTypes.SIGNUP_CONFIRM_SUCCESS,
      actionTypes.SIGNUP_CONFIRM_FAIL,
    ],
    promise: (client) => client.post(url, { otp }, { hideError: true }),
  };
}

export function getRegisterData() {
  return {
    types: [
      actionTypes.GET_REGISTER_DATA,
      actionTypes.GET_REGISTER_DATA_SUCCESS,
      actionTypes.GET_REGISTER_DATA_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/authentication/user-registration/register-data`,
      { hideError: true, authRequest: true }
    ),
  };
}

export function logout(data) {
  const actionObject = {
    types: [
      actionTypes.LOGOUT,
      actionTypes.LOGOUT_SUCCESS,
      actionTypes.LOGOUT_FAIL,
    ],
    promise: (client) => client.delete(
      `${API_HOST}/authentication`,
      { hideError: true, authRequest: true }
    ),
  };

  const cookiesManager = getCookiesManager();
  cookiesManager.saveValue(false, 'ocmEmailConfirmed', 'AU');

  if (data && data.redirect) {
    actionObject.data = { redirectURL: data.redirect };
  } else {
    actionObject.afterSuccess = (dispatch, getState) => {
      const ocmRejection = cookiesManager.getBoolValue('ocmRejection', 'AU');

      if (ocmRejection) {
        cookiesManager.removeValue('ocmRejection', 'AU');
        window.location.href = `${BASE_PATH}rejection`;
      }

      if (COUNTRY === 'LT') {
        dispatch(resetReduxForm('authentication-form'));
      }
      dispatch(reset('loan'));
      dispatch(resetLoanForm());
      if (bdsTrackingAvailable()) {
        removeBdsTracking();
      }
      if (EMBEDDED) {
        if (data && data.sendMessageToParent) {
          dispatch(sendMessageToParent({
            messageType: 'LOAN_APP_EVENT',
            eventType: `${actionTypes.LOGOUT_SUCCESS}`,
          }));
        } else {
          const { integration } = getState();
          dispatch(onIframeMessage({
            data: {
              messageType: 'PARTNER_WEBSITE_PARAMS',
              params: integration,
            },
          }));
        }
      }
    };
  }

  return actionObject;
}

export function setSelfServiceImpersonation() {
  savePersistentItem('isSelfServiceImpersonation', 'true');

  return {
    type: actionTypes.SET_SELF_SERVICE_IMPERSONATION,
  };
}

export function unsetSelfServiceImpersonation() {
  removePersistentItem('isSelfServiceImpersonation');

  return {
    type: actionTypes.UNSET_SELF_SERVICE_IMPERSONATION,
  };
}

export function startOneCentIdentification(afterSuccess = null) {
  return {
    types: [
      actionTypes.START_ONE_CENT_IDENTIFICATION,
      actionTypes.START_ONE_CENT_IDENTIFICATION_SUCCESS,
      actionTypes.START_ONE_CENT_IDENTIFICATION_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}/authentication/user-registration/register-cz/change-bank-account`),
    afterSuccess: () => {
      if (typeof afterSuccess === 'function') {
        afterSuccess();
      }
    },
  };
}

export function startBankLogin(bank, principal, maturity, showLogin, token, location) {
  const queryParams = queryStringParser.parseAndMakeKeysLowerCase(window.location.search);
  queryParams.showLogin = showLogin;
  if (maturity) {
    queryParams.maturity = maturity;
  }
  if (principal) {
    queryParams.principal = (principal / 100).toFixed(0);
  }
  const search = `?${queryStringParser.stringify(queryParams)}`;

  const requestConfig = { hideError: true, authRequest: true };
  if (token) {
    requestConfig.headers = { 'X-MW-token': token };
  }

  return {
    types: [
      actionTypes.BANK_LOGIN_START,
      actionTypes.BANK_LOGIN_START_SUCCESS,
      actionTypes.BANK_LOGIN_START_FAIL,
    ],
    data: {
      bank,
    },
    promise: (client) => {
      let callbackUrl = window.location.origin + window.location.pathname;
      if (EMBEDDED && COUNTRY === 'FI') {
        callbackUrl = window.location.origin + window.location.pathname.replace(new RegExp(`${location.pathname}$`, 'g'), 'bankl.html');
      }
      return client.post(
        `${API_HOST}/authentication/banks_${COUNTRY.toLowerCase()}/${bank}`,
        { callbackUrl },
        requestConfig
      );
    },
    afterSuccess: (dispatch) => {
      cacheQueryString(search);
      if (testLocalStorageSupport()) {
        localStorage.setItem('bankLoginStarted', moment.utc().format());
      }
      if (EMBEDDED && COUNTRY === 'FI') {
        setTimeout(() => {
          dispatch(getUserSession());
        }, 20000);
        dispatch(sendMessageToParent({
          messageType: 'LOAN_APP_EVENT',
          eventType: 'modalDialogScrollTop',
        }));
      }
    },
    afterFailure: (dispatch) => {
      if (MOBILE_WALLET) {
        dispatch(mwSendPageStatus('token expired'));
      }
    },
  };
}

export function getLegacyRedirectUrl() {
  if (COUNTRY === 'LT') {
    return 'https://paskolos.credit24.lt/savitarna.html';
  }
  return null;
}

export function redirectCustomerToSS() {
  if (!EMBEDDED) {
    redirectToSS();
  }
  return {
    type: actionTypes.CUSTOMER_REDIRECT,
  };
}

export function redirectLVCustomerToSS(contracts) {
  const contractId = contracts[0];
  return {
    types: [
      actionTypes.CUSTOMER_REDIRECT,
      actionTypes.GET_CONTRACTS_SUCCESS,
      actionTypes.GET_CONTRACTS_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/contracts/${contractId}`),
    afterSuccess: () => {
      if (!EMBEDDED) {
        redirectToSS();
      }
    },
    afterFailure: () => {
      if (!EMBEDDED) {
        redirectToSS();
      }
    },
  };
}

export function redirectToLoginWithMessage(messageKey) {
  return (dispatch, getState) => {
    const props = getState();
    if (!props.authentication.isRedirecting) {
      dispatch(logout());
      dispatch(displayAuthError(messageKey));
    }
  };
}

export function requestNewPassword() {
  return {
    type: actionTypes.GET_NEW_PASSWORD,
    meta: {
      analyticsLV: () => (
        getAnalyticsObject('forgot')
      ),
    },
  };
}

export function cancelNewPasswordRequest() {
  return {
    type: actionTypes.CANCEL_GET_NEW_PASSWORD,
  };
}

export function cancelTemporaryPasswordLogin() {
  return {
    type: actionTypes.CANCEL_TEMPORARY_PASSWORD_LOGIN,
  };
}

export function sendNewPassword(customerNumber) {
  let url = `${API_HOST}/authentication/request-otp`;
  let data;
  switch (COUNTRY) {
    case 'PL':
      data = { email: customerNumber };
      break;
    case 'LT':
      url = `${url}-lt`;
      if (customerNumber.startsWith('+')) {
        data = { customerNumberOrMsisdn: normalizeMsisdn(customerNumber) };
      } else {
        data = { customerNumberOrMsisdn: customerNumber };
      }
      break;
    default:
      data = { customerNumber };
  }
  return {
    types: [
      actionTypes.SEND_NEW_PASSWORD,
      actionTypes.SEND_NEW_PASSWORD_SUCCESS,
      actionTypes.SEND_NEW_PASSWORD_FAIL,
    ],
    promise: (client) => client.post(
      url,
      data,
      { preventLogout: true, hideError: true }
    ),
    meta: {
      analyticsLV: () => (
        getAnalyticsObject('temppassword')
      ),
    },
  };
}

export function changePassword(password) {
  return {
    types: [
      actionTypes.CHANGE_PASSWORD,
      actionTypes.CHANGE_PASSWORD_SUCCESS,
      actionTypes.CHANGE_PASSWORD_FAIL,
    ],
    promise: (client) => client.put(`${API_HOST}/customer/password`, {
      password,
      checkCurrentPassword: false,
    }),
  };
}

export function changePasswordFirstLogin(data) {
  return {
    types: [
      actionTypes.CHANGE_PASSWORD,
      actionTypes.CHANGE_PASSWORD_SUCCESS,
      actionTypes.CHANGE_PASSWORD_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/customer-es/passwd-first-login`,
      {
        identifier: data.username,
        currentPassword: data.login_pass,
        newPassword: data.newPassword,
      }
    ),
    afterSuccess: (dispatch) => {
      dispatch(
        change('es-auth', 'username', data.username)
      );
    },
  };
}

export function closePasswordChangedFeedback() {
  return {
    type: actionTypes.CHANGE_PASSWORD_COMPLETED,
  };
}

export function initPasswordRecovery(email) {
  return {
    types: [
      actionTypes.INIT_PASSWORD_RECOVERY,
      actionTypes.INIT_PASSWORD_RECOVERY_SUCCESS,
      actionTypes.INIT_PASSWORD_RECOVERY_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/password-recovery/send-email`,
      { email },
      { preventLogout: true, hideError: true }
    ),
    data: {
      email,
    },
    afterSuccess: (_, __, response) => {
      if (COUNTRY === 'AU') {
        if (response.data.error === 'CUSTOMER_NOT_FOUND_OR_NOT_ENABLED') {
          saveLocalStorageItem('invalidRecoveryEmail', email);
        }
      }
    },
  };
}

export function toggleRecoveryPasswordScreen() {
  return {
    type: actionTypes.TOGGLE_RECOVERY_PASSWORD,
  };
}

export function navigateBackToLinkRequest() {
  return {
    type: actionTypes.PASSWORD_LINK_RECOVERY_NAVIGATE_BACK,
  };
}

export function cancelPasswordRecovery() {
  return {
    type: actionTypes.CANCEL_PASSWORD_RECOVERY,
  };
}

export function clearSignUpGate() {
  return {
    type: actionTypes.CLEAR_SIGN_UP_GATE,
  };
}

export function saveSignUpData(data, partnerId, adtractionChannel, marketingConsents = [], locale = '') {
  const transformedData = { ...data };

  if (COUNTRY !== 'MX') {
    const firstLastName = data.sugName ? data.sugName.split(' ') : [];
    if (firstLastName.length > 0) {
      transformedData.sugLastName = firstLastName.slice(-1).join();
    }
    if (firstLastName.length > 1) {
      transformedData.sugFirstName = firstLastName.slice(0, -1).join(' ');
    }
  }
  saveSignUpGate(transformedData);

  const formData = [
    {
      name: 'oid',
      value: '00DD0000000plZX',
    },
    {
      name: 'retURL',
      value: window.location.href.split('?')[0],
    },
    {
      name: '00N570000069ufm',
      value: transformedData.sugMarketingPermission ? 1 : 0,
    },
    {
      name: '00N5700000605ok',
      value: COUNTRY,
    },
    {
      name: 'email',
      value: transformedData.sugEmail,
    },
  ];

  switch (COUNTRY) {
    case 'MX':

      formData.push(...[
        {
          name: 'first_name',
          value: transformedData.sugFirstName,
        },
        {
          // second first name
          name: '00N57000006JOBE',
          value: transformedData.sugSecondName,
        },
        {
          // first last name (paternal)
          name: 'last_name',
          value: transformedData.sugPaternalName,
        },
        {
          // second last name (maternal)
          name: '00N57000006JOBJ',
          value: transformedData.sugMaternalName,
        },
      ]);
      break;
    default:
      formData.push(...[
        {
          name: 'first_name',
          value: transformedData.sugFirstName,
        },
        {
          name: 'last_name',
          value: transformedData.sugLastName,
        },
        {
          name: 'phone',
          value: transformedData.sugMsisdn,
        },
        {
          name: '00N57000006Hb5A',
          value: partnerId,
        },
      ]);
  }

  if (COUNTRY === 'FI') {
    const lowerCasePartnerID = partnerId && partnerId.toLowerCase();
    if (lowerCasePartnerID === 'adtraction' && adtractionChannel === '1212940008') {
      formData.push({
        name: '00N570000069ufp',
        value: transformedData.sugAdtractionMarketingPermission ? 1 : 0,
      });
    }
    formData.push({
      name: 'CustomerConsents__c',
      value: JSON.stringify(generateConsents(marketingConsents, transformedData, locale)),
    });
  }

  const iframe = document.createElement('iframe');
  iframe.name = 'signupgate';
  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8';
  form.target = iframe.name;
  form.style.display = 'none';

  formData.forEach((field) => {
    const node = document.createElement('input');
    node.type = 'hidden';
    node.name = field.name;
    node.value = field.value;
    form.appendChild(node);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);

  return {
    type: actionTypes.SAVE_SIGN_UP_DATA,
  };
}

export function getLocalTime() {
  return {
    types: [
      actionTypes.GET_LOCAL_TIME,
      actionTypes.GET_LOCAL_TIME_SUCCESS,
      actionTypes.GET_LOCAL_TIME_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/authentication/user-registration/get-time`,
      { hideError: true }
    ),
    afterFailure: () => {
      getLocalTime();
    },
  };
}

export function setMobileWalletToken(token) {
  return {
    type: actionTypes.SET_MW_TOKEN,
    data: token,
  };
}

export function checkMobileWalletToken(token) {
  return {
    types: [
      actionTypes.CHECK_MW_TOKEN,
      actionTypes.CHECK_MW_TOKEN_SUCCESS,
      actionTypes.CHECK_MW_TOKEN_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/mw/authentication`,
      null,
      {
        headers: { 'X-MW-token': token },
        authRequest: true,
        preventLogout: true,
        hideError: true,
      }
    ),
    afterFailure: (dispatch, getState, response) => {
      const { status } = response;
      if (status === 404) {
        dispatch(mwSendPageStatus('token expired'));
      }
    },
  };
}

export function setCPLTriggered() {
  return {
    type: actionTypes.SET_CPL_TRIGGERED,
  };
}

export function requestOtpCZ() {
  return {
    types: [
      actionTypes.INIT_OTP,
      actionTypes.INIT_OTP_SUCCESS,
      actionTypes.INIT_OTP_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/authentication/user-registration/register-cz/send-otp-request`,
      { hideError: true }
    ),
    meta: {
      analyticsCZ: (storeState) => (
        getAnalyticsObject('getPinCodeCZ', storeState)
      ),
    },
  };
}

export function requestOTPCzChangeProduct() {
  const { authentication: { confirmationKey } } = store.getState();
  const payloadRequestOtp = { token: confirmationKey };
  const requestOtpUrl = `${API_HOST}/credit-application-cz/product-change/request-otp`;
  return {
    types: [
      actionTypes.VALIDATE_OTP,
      actionTypes.VALIDATE_OTP_FAIL,
    ],
    promise: (client) => client.post(
      requestOtpUrl,
      payloadRequestOtp,
      { hideError: true }
    ),
    afterFailure: (dispatch) => {
      dispatch(
        stopSubmit('loan', { otp: 'errors.field.otp.invalid' })
      );
    },
    afterSuccess: (dispatch) => {
      dispatch(
        stopSubmit('loan', { otp: 'errors.field.otp.invalid' })
      );
    },
  };
}

export function makeUserChangingPassword() {
  return {
    type: actionTypes.MAKE_USER_CHANGING_PASSWORD,
  };
}

export function updateCustomerEmail(email, afterSuccess) {
  if (COUNTRY === 'AU') {
    if (email) {
      return {
        types: [
          actionTypes.PUT_CUSTOMER_EMAIL,
          actionTypes.PUT_CUSTOMER_EMAIL_SUCCESS,
          actionTypes.PUT_CUSTOMER_EMAIL_FAIL,
        ],
        promise: (client) => client.put(
          `${API_HOST}/customer-au/email`,
          { email },
          { hideError: true }
        ),
        afterSuccess: (dispatch, getState, response) => {
          if (typeof afterSuccess === 'function') {
            afterSuccess(response.data.updateResult);
          }
        },
      };
    }
  }
  const data = { action: 'updateCustomerEmail' };
  if (DEBUG) {
    data.reason = 'no email provided or country is not supported';
  }
  return {
    data,
    type: actionTypes.ACTION_SKIPPED,
  };
}

export function resetBankLogin() {
  return {
    type: actionTypes.RESET_BANK_LOGIN,
  };
}

export function setLandingUrl(landingUrl) {
  return {
    type: actionTypes.SET_LANDING_URL,
    data: landingUrl,
  };
}

export function setRegistrationStep(data) {
  return {
    type: actionTypes.SET_REGISTRATION_STEP,
    data,
  };
}

export function setBankPaymentButtonClicked(data) {
  return {
    type: actionTypes.SET_BANK_PAYMENT_BUTTON_CLICKED,
    data,
  };
}

export function showIPaidButton(data) {
  return {
    type: actionTypes.SHOW_I_PAID_BUTTON,
    data,
  };
}

export const getCustomerConsents = (channel, frontPagePrincipal) => ({
  types: [
    actionTypes.GET_CONSENTS,
    actionTypes.GET_CONSENTS_SUCCESS,
    actionTypes.GET_CONSENTS_FAIL,
  ],
  promise: (client) => client.get(
    `${API_HOST}/customer/additional-consent`,
    { hideError: true }
  ),
  afterFailure: (dispatch) => {
    dispatch(submitInitLoanStep(
      channel,
      frontPagePrincipal
    ));
  },
  afterSuccess: (dispatch) => {
    dispatch(submitInitLoanStep(
      channel,
      frontPagePrincipal
    ));
  },
});

export const checkEmailVerificationStatus = () => ({
  types: [
    actionTypes.CHECK_EMAIL_VERIFICATION_STATUS,
    actionTypes.CHECK_EMAIL_VERIFICATION_STATUS_SUCCESS,
    actionTypes.CHECK_EMAIL_VERIFICATION_STATUS_FAIL,
  ],
  promise: (client) => client.get(`${API_HOST}/customer-au/email-verification/status`, { hideError: true }),
  afterSuccess: (dispatch, getState, response) => {
    const isVerified = response.data.status === EMAIL_VERIFICATION_STATUS.VERIFIED;
    if (isVerified) {
      dispatch(goToSpecificStep(STEP_SUBMIT.type));
    }
  },
});

export const sendConfirmationEmail = () => ({
  types: [
    actionTypes.SEND_CONFIRMATION_EMAIL,
    actionTypes.SEND_CONFIRMATION_EMAIL_SUCCESS,
    actionTypes.SEND_CONFIRMATION_EMAIL_FAIL,
  ],
  promise: (client) => client.post(`${API_HOST}/customer-au/resend-email`, null, { hideError: true }),
});
